import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Plus, Trash2, X, Save, Eye, EyeOff, LayoutGrid, Sparkles } from "lucide-react";
import * as LI from "lucide-react";
import { SERVICE_ICONS } from "../../components/public/ServicesGrid";

const LANG_TABS = [
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
];

function IconFor(name) {
  return LI[name] || LI.Sparkles;
}

export default function HomeServicesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("es");
  const [translating, setTranslating] = useState(false);

  const load = () => api.get("/admin/home-services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const blank = () => ({
    icon: "FileText",
    title_es: "", title_fr: "", title_en: "", title_ar: "",
    description_es: "", description_fr: "", description_en: "", description_ar: "",
    url: "/servicios", order: (items.length || 0) + 1, active: true,
  });

  const save = async () => {
    setError("");
    try {
      if (editing.id) {
        const { id, ...body } = editing;
        await api.patch(`/admin/home-services/${id}`, body);
      } else {
        await api.post("/admin/home-services", editing);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este servicio de la portada?")) return;
    await api.delete(`/admin/home-services/${id}`);
    load();
  };

  const toggle = async (item) => {
    const { id, ...body } = item;
    await api.patch(`/admin/home-services/${id}`, { ...body, active: !item.active });
    load();
  };

  // Fill the current tab's title/description by translating from ES via the
  // AI-translate endpoint. No-op when the target is ES or when ES fields are empty.
  const aiTranslateCurrent = async () => {
    if (!editing || tab === "es") return;
    setTranslating(true);
    setError("");
    try {
      const toTranslate = [
        { key: "title", src: editing.title_es },
        { key: "description", src: editing.description_es },
      ].filter((f) => f.src && String(f.src).trim());
      for (const f of toTranslate) {
        const { data } = await api.post("/admin/translate", {
          text: f.src,
          source_lang: "es",
          target_lang: tab,
        });
        if (data?.translation) {
          setEditing((prev) => ({ ...prev, [`${f.key}_${tab}`]: data.translation }));
        }
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div data-testid="cms-home-services">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040] flex items-center gap-2">
            <LayoutGrid className="w-5 h-5" /> Servicios del Home
          </h1>
          <p className="text-sm text-gray-600">Grid &quot;Nuestros Servicios&quot; que aparece en la portada. Activar/desactivar y modificar cada tarjeta.</p>
        </div>
        <button
          onClick={() => setEditing(blank())}
          data-testid="home-services-add"
          className="bg-[#C8102E] text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-[#A30D25]"
        >
          <Plus className="w-4 h-4" /> Nuevo servicio
        </button>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
            <tr>
              <th className="text-left px-5 py-3 w-16">Icono</th>
              <th className="text-left px-5 py-3">Título (ES)</th>
              <th className="text-left px-5 py-3">URL</th>
              <th className="text-left px-5 py-3 w-20">Orden</th>
              <th className="text-left px-5 py-3 w-24">Estado</th>
              <th className="text-right px-5 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-gray-400" data-testid="home-services-empty">Todavía no hay servicios.</td></tr>
            ) : items.map((s) => {
              const Icon = IconFor(s.icon);
              return (
                <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`home-service-row-${s.id}`}>
                  <td className="px-5 py-3"><Icon className="w-5 h-5 text-[#C8102E]" strokeWidth={1.4} /></td>
                  <td className="px-5 py-3 font-medium text-[#0F2040]">{s.title_es}</td>
                  <td className="px-5 py-3 text-xs text-gray-500 font-mono">{s.url || "—"}</td>
                  <td className="px-5 py-3 text-gray-600">{s.order}</td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => toggle(s)}
                      data-testid={`home-service-toggle-${s.id}`}
                      className={`text-xs px-2 py-1 border inline-flex items-center gap-1 ${
                        s.active
                          ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                          : "border-gray-200 text-gray-500 bg-gray-50"
                      }`}
                    >
                      {s.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {s.active ? "Activo" : "Oculto"}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing({ ...s })}
                      data-testid={`home-service-edit-${s.id}`}
                      className="text-xs px-3 py-1.5 border border-gray-200 hover:border-[#0F2040] mr-2"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => remove(s.id)}
                      data-testid={`home-service-delete-${s.id}`}
                      className="p-1.5 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="home-service-modal">
          <div className="bg-white max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="font-serif text-xl text-[#0F2040]">
                {editing.id ? "Editar servicio" : "Nuevo servicio"}
              </h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wider">Icono</label>
                <div className="grid grid-cols-8 gap-2" data-testid="icon-picker">
                  {SERVICE_ICONS.map((name) => {
                    const Icon = IconFor(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setEditing((prev) => ({ ...prev, icon: name }))}
                        data-testid={`icon-option-${name}`}
                        title={name}
                        className={`aspect-square flex items-center justify-center border ${
                          editing.icon === name
                            ? "border-[#C8102E] bg-red-50 text-[#C8102E]"
                            : "border-gray-200 text-gray-500 hover:border-[#0F2040]"
                        }`}
                      >
                        <Icon className="w-5 h-5" strokeWidth={1.4} />
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Actual: <code>{editing.icon}</code></p>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <Field label="URL del enlace" value={editing.url} onChange={(v) => setEditing((p) => ({ ...p, url: v }))} testid="url" />
                <Field label="Orden" type="number" value={editing.order} onChange={(v) => setEditing((p) => ({ ...p, order: Number(v) || 0 }))} testid="order" />
              </div>

              {/* Language tabs for title + description */}
              <div className="border border-gray-200">
                <div className="border-b border-gray-200 flex items-center gap-1 flex-wrap bg-gray-50" data-testid="home-service-lang-tabs">
                  {LANG_TABS.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => setTab(l.code)}
                      data-testid={`home-service-tab-${l.code}`}
                      className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        tab === l.code
                          ? "border-[#C8102E] text-[#0F2040] bg-white"
                          : "border-transparent text-gray-500 hover:text-[#0F2040]"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                  {tab !== "es" && editing.title_es && (
                    <button
                      type="button"
                      onClick={aiTranslateCurrent}
                      disabled={translating}
                      title={`Rellenar título y descripción en ${tab.toUpperCase()} traduciendo desde ES con IA`}
                      data-testid="home-service-ai-translate"
                      className="ml-auto mr-2 my-1 inline-flex items-center gap-1.5 text-xs text-[#C8102E] border border-[#C8102E]/40 px-3 py-1.5 hover:bg-[#C8102E] hover:text-white transition-colors disabled:opacity-50"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {translating ? "Traduciendo…" : `Traducir con IA → ${tab.toUpperCase()}`}
                    </button>
                  )}
                </div>
                <div className="p-4 space-y-3" dir={tab === "ar" ? "rtl" : "ltr"}>
                  <Field
                    label={`Título (${tab.toUpperCase()})${tab === "es" ? " *" : ""}`}
                    value={editing[`title_${tab}`]}
                    onChange={(v) => setEditing((p) => ({ ...p, [`title_${tab}`]: v }))}
                    testid={`title-${tab}`}
                  />
                  <Field
                    label={`Descripción (${tab.toUpperCase()})`}
                    value={editing[`description_${tab}`]}
                    onChange={(v) => setEditing((p) => ({ ...p, [`description_${tab}`]: v }))}
                    testid={`desc-${tab}`}
                  />
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-[#0F2040] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.active}
                  onChange={(e) => setEditing((p) => ({ ...p, active: e.target.checked }))}
                  className="accent-[#C8102E]"
                  data-testid="active-toggle"
                />
                Activo (visible en la portada)
              </label>

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={save}
                data-testid="home-service-save"
                className="bg-[#C8102E] text-white px-5 py-2 text-sm inline-flex items-center gap-2 hover:bg-[#A30D25]"
              >
                <Save className="w-4 h-4" /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
      />
    </div>
  );
}
